<script setup lang="ts">
import { h, computed, resolveComponent, ref, type Component } from 'vue'
import type { Brand, PlatformAdvertiser } from '@/stores/admin'
import { useAdmin } from '@/stores/admin'
import type { PlatformImportStats } from '@/stores/imports'
import type { TableColumn } from '@nuxt/ui'
import { formatRelativeTime, getImportStatusColor } from '@/lib/utils'
import CountryFlag from '@/components/CountryFlag.vue'
import PlatformBadge from '@/components/PlatformBadge.vue'
import { createSortableHeader, createCopyableIdCell } from '@/lib/tableHelpers'

const props = defineProps<{
  brands: Brand[]
  selectedBrandIds?: string[]
  ownedBrandIds?: string[]
  brandAdvertisers?: Map<string, PlatformAdvertiser[]>
  brandImportStats?: Map<string, PlatformImportStats[]>
  loading?: boolean
}>()

const emit = defineEmits<{
  select: [brandId: string]
  settings: [brand: Brand]
  viewImports: [brand: Brand]
  toggleOwnership: [brandId: string]
}>()

const admin = useAdmin()

// Lookup DB name from code so PlatformBadge can render the canonical
// display label (e.g. `X (Twitter)`) rather than the raw code.
// O(1) lookup via a Map computed from admin.platforms (a linear scan
// per rendered badge was fine at N<15 platforms but doesn't scale, and
// this table renders 3 badges per brand row — quickly O(N × M)).
const platformByCode = computed(() => new Map(admin.platforms.map((p) => [p.code, p])))
function platformName(code: string): string | undefined {
  return platformByCode.value.get(code)?.name
}

const UAvatar = resolveComponent('UAvatar')
const UBadge = resolveComponent('UBadge')
const UButton = resolveComponent('UButton') as Component
const UCheckbox = resolveComponent('UCheckbox')
const UIcon = resolveComponent('UIcon')
const UTooltip = resolveComponent('UTooltip')

// Create sortable header factory with resolved UButton
const sortable = createSortableHeader(UButton)
const copyableId = createCopyableIdCell(UButton)

// Table sorting state (native UTable/TanStack)
const sorting = ref([{ id: 'name', desc: false }])

// Group advertisers by platform, then by advertiser ID
function groupAdvertisersByPlatform(advertisers: PlatformAdvertiser[]) {
  const byPlatform: Record<
    string,
    {
      platformCode: string
      platformName: string
      advertisers: { id: string; countries: (string | null)[] }[]
    }
  > = {}

  for (const adv of advertisers) {
    if (!byPlatform[adv.platform_code]) {
      byPlatform[adv.platform_code] = {
        platformCode: adv.platform_code,
        platformName: adv.platform_name,
        advertisers: [],
      }
    }

    const existing = byPlatform[adv.platform_code].advertisers.find(
      (a) => a.id === adv.platform_advertiser_identifier,
    )
    if (existing) {
      existing.countries.push(adv.country_code)
    } else {
      byPlatform[adv.platform_code].advertisers.push({
        id: adv.platform_advertiser_identifier,
        countries: [adv.country_code],
      })
    }
  }

  return Object.values(byPlatform)
}

const columns = computed<TableColumn<Brand>[]>(() => {
  const cols: TableColumn<Brand>[] = []

  // Selection checkbox (if selectedBrandIds provided)
  if (props.selectedBrandIds !== undefined) {
    cols.push({
      id: 'select',
      header: '',
      size: 40,
      cell: ({ row }) => {
        const isSelected = props.selectedBrandIds?.includes(row.original.id)
        const isOwned = props.ownedBrandIds?.includes(row.original.id)
        // Green for Org, yellow for Tracked, default for unselected
        const color = isSelected ? (isOwned ? 'success' : 'warning') : 'neutral'
        return h(UCheckbox, {
          modelValue: isSelected,
          color,
          'onUpdate:modelValue': () => emit('select', row.original.id),
        })
      },
    })
  }

  // Type column (org brand vs tracked brand)
  if (props.ownedBrandIds !== undefined) {
    cols.push({
      id: 'type',
      accessorFn: (row) => {
        const isSelected = props.selectedBrandIds?.includes(row.id) ?? false
        if (!isSelected) return -1 // Unselected sorts last (asc) or first (desc)
        return props.ownedBrandIds?.includes(row.id) ? 1 : 0 // 1=Org, 0=Tracked
      },
      header: sortable('Type'),
      size: 90,
      cell: ({ row }) => {
        const isOwned = props.ownedBrandIds?.includes(row.original.id) ?? false
        const isSelected = props.selectedBrandIds?.includes(row.original.id) ?? false
        if (!isSelected) {
          return h('span', { class: 'text-muted text-sm' }, '—')
        }
        return h(
          UTooltip,
          { text: isOwned ? 'Org brand (click to change)' : 'Tracked brand (click to change)' },
          () =>
            h(
              'button',
              {
                class: `flex items-center gap-1 text-sm transition-colors ${isOwned ? 'text-[var(--ui-success)] hover:text-[var(--ui-success)]/80' : 'text-[var(--ui-primary)] hover:text-[var(--ui-primary)]/80'}`,
                onClick: () => emit('toggleOwnership', row.original.id),
              },
              [
                h(UIcon, {
                  name: isOwned ? 'i-lucide-building-2' : 'i-lucide-target',
                  class: 'w-4 h-4',
                }),
                isOwned ? 'Org' : 'Tracked',
              ],
            ),
        )
      },
    })
  }

  // Logo
  cols.push({
    accessorKey: 'logo_url',
    header: '',
    size: 50,
    cell: ({ row }) =>
      h(UAvatar, {
        src: row.original.logo_url ?? undefined,
        alt: row.original.name,
        size: 'sm',
      }),
  })

  // Name (sortable, clickable) with view icon
  cols.push({
    accessorKey: 'name',
    header: sortable('Brand'),
    size: 180,
    cell: ({ row }) =>
      h('div', { class: 'flex items-center gap-1' }, [
        h(
          'button',
          {
            class: 'text-left font-medium hover:text-primary transition-colors truncate',
            onClick: () => emit('settings', row.original),
          },
          row.original.name,
        ),
        h(UButton, {
          icon: 'i-lucide-eye',
          size: 'xs',
          variant: 'ghost',
          color: 'neutral',
          class: 'shrink-0',
          onClick: () => emit('settings', row.original),
        }),
      ]),
  })

  // Sectors
  cols.push({
    id: 'sectors',
    accessorFn: (row) => row.sectors.length,
    header: sortable('Sectors'),
    size: 200,
    cell: ({ row }) => {
      const sectors = row.original.sectors
      if (!sectors || sectors.length === 0) {
        return h('span', { class: 'text-muted text-sm' }, '—')
      }
      return h(
        'div',
        { class: 'flex flex-wrap gap-1' },
        sectors.map((s) =>
          h(UBadge, { color: 'neutral', variant: 'outline', size: 'xs' }, () => [
            s.emoji ? h('span', { class: 'mr-0.5' }, s.emoji) : null,
            s.name,
          ]),
        ),
      )
    },
  })

  // ID (copyable)
  cols.push({
    accessorKey: 'id',
    header: 'ID',
    size: 180,
    cell: ({ row }) => copyableId(row.original.id),
  })

  // Platforms with advertisers (sortable by count)
  cols.push({
    id: 'platforms',
    accessorFn: (row) => row.platform_codes.length,
    header: sortable('Platforms'),
    size: 280,
    cell: ({ row }) => {
      const advertisers = props.brandAdvertisers?.get(row.original.id)

      // If loading and no data yet, show skeleton
      if (props.loading && !advertisers) {
        return h('div', { class: 'flex flex-col gap-1' }, [
          h('div', { class: 'h-5 w-24 bg-muted/30 rounded animate-pulse' }),
        ])
      }

      // If no advertisers data, show dash
      if (!advertisers || advertisers.length === 0) {
        return h('span', { class: 'text-muted text-sm' }, '—')
      }

      // Group and render: [PLATFORM] | flags (two columns aligned)
      const grouped = groupAdvertisersByPlatform(advertisers)
      return h(
        'div',
        { class: 'flex flex-col gap-1' },
        grouped.map((platform) => {
          // Collect all unique countries
          const allCountries = new Set<string | null>()
          platform.advertisers.forEach((adv) => adv.countries.forEach((c) => allCountries.add(c)))
          const sortedCountries = [...allCountries].sort((a, b) =>
            a === null ? -1 : b === null ? 1 : a.localeCompare(b),
          )

          return h('div', { class: 'flex items-center gap-2' }, [
            // Platform badge (fixed width column)
            h('div', { class: 'w-20 shrink-0' }, [
              h(PlatformBadge, { code: platform.platformCode, name: platform.platformName }),
            ]),
            // Advertiser count (fixed width, right-aligned)
            h(
              'span',
              { class: 'w-6 text-xs text-muted text-right shrink-0' },
              `${platform.advertisers.length}`,
            ),
            // Flags column (max 6 visible, then +N)
            h('div', { class: 'flex flex-wrap gap-0.5 items-center' }, [
              ...sortedCountries
                .slice(0, 6)
                .map((countryCode) => h(CountryFlag, { code: countryCode })),
              ...(sortedCountries.length > 6
                ? [
                    h(
                      'span',
                      { class: 'text-xs text-muted ml-1' },
                      `+${sortedCountries.length - 6}`,
                    ),
                  ]
                : []),
            ]),
          ])
        }),
      )
    },
  })

  // Latest import per platform (shows status + last successful if latest was not successful)
  cols.push({
    id: 'latestImport',
    accessorFn: (row) => {
      const stats = props.brandImportStats?.get(row.id) || []
      const times = stats.map((s) => s.latestTs).filter(Boolean) as string[]
      return times.length > 0 ? Math.max(...times.map((t) => new Date(t).getTime())) : 0
    },
    header: sortable('Latest import'),
    size: 280,
    cell: ({ row }) => {
      const stats = props.brandImportStats?.get(row.original.id)
      if (!stats || stats.length === 0) {
        return h('span', { class: 'text-muted text-sm' }, '—')
      }
      const withData = stats.filter((s) => s.latestTs)
      if (withData.length === 0) {
        return h('span', { class: 'text-muted text-sm' }, '—')
      }
      return h(
        'div',
        { class: 'flex flex-col gap-1' },
        withData.map((s) => {
          const isLatestSuccessful = s.latestStatus === 'COMPLETED'
          const rows = [
            // Latest import row
            h('div', { class: 'flex items-center gap-2' }, [
              h('div', { class: 'w-16 shrink-0' }, [
                h(PlatformBadge, { code: s.platformCode, name: platformName(s.platformCode) }),
              ]),
              s.latestStatus
                ? h(
                    UBadge,
                    {
                      color: getImportStatusColor(s.latestStatus),
                      variant: 'subtle',
                      size: 'xs',
                    },
                    () => s.latestStatus,
                  )
                : null,
              h('span', { class: 'text-sm text-muted' }, formatRelativeTime(s.latestTs)),
            ]),
          ]
          // If latest was not successful, show last successful below
          if (!isLatestSuccessful && s.lastSuccessfulTs) {
            rows.push(
              h('div', { class: 'flex items-center gap-2 ml-[4.5rem]' }, [
                h('span', { class: 'text-xs text-muted' }, 'Last success:'),
                h('span', { class: 'text-xs' }, formatRelativeTime(s.lastSuccessfulTs)),
              ]),
            )
          }
          return h('div', { class: 'flex flex-col gap-0.5' }, rows)
        }),
      )
    },
  })

  // Last import with ads per platform
  cols.push({
    id: 'lastWithAds',
    accessorFn: (row) => {
      const stats = props.brandImportStats?.get(row.id) || []
      const times = stats.map((s) => s.lastWithAdsTs).filter(Boolean) as string[]
      return times.length > 0 ? Math.max(...times.map((t) => new Date(t).getTime())) : 0
    },
    header: sortable('Last import with ads'),
    size: 200,
    cell: ({ row }) => {
      const stats = props.brandImportStats?.get(row.original.id)
      if (!stats || stats.length === 0) {
        return h('span', { class: 'text-muted text-sm' }, '—')
      }
      const withAds = stats.filter((s) => s.lastWithAdsTs)
      if (withAds.length === 0) {
        return h('span', { class: 'text-muted text-sm' }, '—')
      }
      return h(
        'div',
        { class: 'flex flex-col gap-0.5' },
        withAds.map((s) =>
          h('div', { class: 'flex items-center gap-2' }, [
            h('div', { class: 'w-16 shrink-0' }, [
              h(PlatformBadge, { code: s.platformCode, name: platformName(s.platformCode) }),
            ]),
            h('span', { class: 'text-sm' }, formatRelativeTime(s.lastWithAdsTs)),
          ]),
        ),
      )
    },
  })

  // Last ads count per platform
  cols.push({
    id: 'lastAdsCount',
    accessorFn: (row) => {
      const stats = props.brandImportStats?.get(row.id) || []
      return stats.reduce((sum, s) => sum + (s.lastAdsCount || 0), 0)
    },
    header: sortable('Last # of ads > 0'),
    size: 160,
    cell: ({ row }) => {
      const stats = props.brandImportStats?.get(row.original.id)
      if (!stats || stats.length === 0) {
        return h('span', { class: 'text-muted text-sm' }, '—')
      }
      const withAds = stats.filter((s) => s.lastAdsCount != null && s.lastAdsCount > 0)
      if (withAds.length === 0) {
        return h('span', { class: 'text-muted text-sm' }, '—')
      }
      return h(
        'div',
        { class: 'flex flex-col gap-0.5' },
        withAds.map((s) =>
          h('div', { class: 'flex items-center gap-2' }, [
            h('div', { class: 'w-16 shrink-0' }, [
              h(PlatformBadge, { code: s.platformCode, name: platformName(s.platformCode) }),
            ]),
            h('span', { class: 'text-sm font-medium' }, s.lastAdsCount!.toString()),
          ]),
        ),
      )
    },
  })

  // Actions column
  cols.push({
    id: 'actions',
    header: 'Actions',
    size: 160,
    cell: ({ row }) =>
      h('div', { class: 'flex gap-1' }, [
        h(
          UButton,
          {
            icon: 'i-lucide-settings',
            color: 'neutral',
            variant: 'ghost',
            size: 'xs',
            onClick: () => emit('settings', row.original),
          },
          () => 'Manage',
        ),
        h(
          UButton,
          {
            icon: 'i-lucide-download',
            color: 'neutral',
            variant: 'ghost',
            size: 'xs',
            onClick: () => emit('viewImports', row.original),
          },
          () => 'Imports',
        ),
      ]),
  })

  return cols
})
</script>

<template>
  <UTable :columns="columns" :data="brands" :loading="loading" v-model:sorting="sorting">
    <template #empty>
      <div class="text-center py-6 text-muted">No brands</div>
    </template>
  </UTable>
</template>
